import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, UsersRound, Ticket, FileText,
  Medal, Trophy, WalletCards, Settings, LogOut,
  ChevronsLeft, ChevronsRight, ChevronDown, BriefcaseBusiness,
  Database, SlidersHorizontal, UserRound,
  ScrollText, Menu, X,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const directNav = [
  { icon: LayoutDashboard, label: "Overview", to: "/admin", end: true },
];

const navGroups = [
  {
    id: "operations",
    label: "Operations",
    icon: BriefcaseBusiness,
    items: [
      { icon: Ticket, label: "Pools", to: "/admin/pools" },
      { icon: FileText, label: "Tickets / Requests", to: "/admin/tickets" },
      { icon: Medal, label: "Results", to: "/admin/results" },
    ],
  },
  {
    id: "records",
    label: "Records",
    icon: Database,
    items: [
      { icon: UsersRound, label: "Users", to: "/admin/users" },
      { icon: Trophy, label: "Winners", to: "/admin/winners" },
      { icon: WalletCards, label: "Payments", to: "/admin/payments" },
    ],
  },
  {
    id: "system",
    label: "System",
    icon: SlidersHorizontal,
    items: [
      { icon: ScrollText, label: "Audit Logs", to: "/admin/audit-logs" },
      { icon: Settings, label: "Settings", to: "/admin/settings" },
      { icon: UserRound, label: "Profile", to: "/admin/profile" },
    ],
  },
];

export default function AdminSidebar() {
  const [collapsed, setCollapsed] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const logoUrl = `${process.env.PUBLIC_URL}/logo.png`;
  const activeGroup = navGroups.find((group) =>
    group.items.some((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`))
  )?.id;
  const [openGroups, setOpenGroups] = React.useState(() => ({
    operations: activeGroup === "operations",
    records: activeGroup === "records",
    system: activeGroup === "system",
  }));
  const initials = user?.name
    ? user.name
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "A";

  React.useEffect(() => {
    if (!activeGroup) return;
    setOpenGroups((current) => ({ ...current, [activeGroup]: true }));
  }, [activeGroup]);

  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  function handleLogout() {
    logout();
    navigate("/login");
  }

  function toggleGroup(groupId) {
    setOpenGroups((current) => ({ ...current, [groupId]: !current[groupId] }));
  }

  return (
    <aside className={`admin-sidebar ${collapsed ? "collapsed" : ""} ${mobileOpen ? "mobile-open" : ""}`}>
      <div className="admin-brand-row">
        <div className="brand admin-brand">
          <img src={logoUrl} alt="Lucky Pool logo" className="admin-brand-logo" />
          {/* <div className="admin-brand-text">
            <strong>Lucky Pool</strong>
            <span>Admin Command Center</span>
          </div> */}
        </div>
        <button
          className="sidebar-toggle"
          type="button"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? <ChevronsRight size={17} /> : <ChevronsLeft size={17} />}
        </button>
        <button
          className="admin-mobile-menu"
          type="button"
          aria-label={mobileOpen ? "Close admin navigation" : "Open admin navigation"}
          aria-expanded={mobileOpen}
          aria-controls="admin-primary-navigation"
          onClick={() => setMobileOpen((open) => !open)}
        >
          {mobileOpen ? <X size={19} /> : <Menu size={19} />}
        </button>
      </div>
      <nav id="admin-primary-navigation">
        {directNav.map(({ icon: Icon, label, to, end }) => (
          <NavLink
            key={label}
            to={to}
            end={end}
            className={({ isActive }) => isActive ? "active" : ""}
            data-label={label}
            aria-label={label}
          >
            <Icon size={18} />
            <span>{label}</span>
          </NavLink>
        ))}
        {navGroups.map((group) => {
          const GroupIcon = group.icon;
          const isOpen = collapsed || openGroups[group.id];
          const isActiveGroup = activeGroup === group.id;

          return (
            <div
              className={`sidebar-group ${isOpen ? "open" : ""} ${isActiveGroup ? "active-group" : ""}`}
              key={group.id}
            >
              <button
                className="sidebar-group-toggle"
                type="button"
                onClick={() => toggleGroup(group.id)}
                data-label={group.label}
                aria-expanded={isOpen}
                aria-label={group.label}
              >
                <GroupIcon size={18} />
                <span>{group.label}</span>
                <ChevronDown className="sidebar-group-chevron" size={15} />
              </button>
              <div className="sidebar-group-menu" aria-label={`${group.label} navigation`}>
                {group.items.map(({ icon: Icon, label, to }) => (
                  <NavLink
                    key={label}
                    to={to}
                    className={({ isActive }) => isActive ? "active" : ""}
                    data-label={label}
                    aria-label={label}
                  >
                    <Icon size={17} />
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="admin-profile">
        <NavLink to="/admin/profile" className={({ isActive }) => isActive ? "admin-profile-identity active" : "admin-profile-identity"} aria-label="View profile">
          <span className="avatar small">{initials}</span>
          <p><strong>{user?.name || "Admin"}</strong><small>Master</small></p>
        </NavLink>
        <button
          className="admin-profile-logout"
          onClick={handleLogout}
          data-label="Sign Out"
          aria-label="Sign Out"
        >
          <LogOut size={15} /><span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
